// =============================================================================
// VandieZ admin-web -- Bid-review call detail (/bid-calls/:callId)
// =============================================================================
// Single Exotel call row + recording URL. Recordings are stored by
// Exotel behind basic-auth so we surface the URL as a copyable text +
// "open in new tab" link rather than embedding the audio.
// =============================================================================

import {
  AlertCircle,
  ArrowLeft,
  Copy,
  ExternalLink,
  Loader2,
  PhoneCall,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { bidCallsAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type { AdminBidCallDetail } from "@/types/bid_calls";

const STATUS_PILL: Record<string, string> = {
  completed: "bg-success-subtle text-success-subtle-foreground",
  in_progress: "bg-primary-subtle text-primary-subtle-foreground",
  initiated: "bg-muted text-muted-foreground",
  failed: "bg-danger-subtle text-danger-subtle-foreground",
};

function formatDateTime(iso: string | null): string {
  if (iso === null) {return "—";}
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function formatDuration(sec: number | null): string {
  if (sec === null) {return "—";}
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export function BidCallDetailPage(): React.JSX.Element {
  const { callId } = useParams<{ callId: string }>();
  const { accessToken } = useAuth();
  const [row, setRow] = useState<AdminBidCallDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<boolean>(false);

  const fetchRow = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (accessToken === null || callId === undefined) {return;}
      setLoading(true);
      setError(null);
      try {
        const res = await bidCallsAdminApi.detail(accessToken, callId, signal);
        setRow(res);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {return;}
        if (e instanceof ApiError) {setError(e.problem.detail);}
        else {setError("Unexpected error. Try again.");}
      } finally {
        setLoading(false);
      }
    },
    [accessToken, callId],
  );

  useEffect(() => {
    const c = new AbortController();
    void fetchRow(c.signal);
    return () => { c.abort(); };
  }, [fetchRow]);

  const copyUrl = async (url: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => { setCopied(false); }, 1500);
    } catch {
      // ignore — fallback is the right-click-copy on the link
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Bid call detail"
        description={
          <Link
            to="/bid-calls"
            className="inline-flex items-center text-sm hover:underline"
          >
            <ArrowLeft className="mr-1 h-3 w-3" />
            Back to call log
          </Link>
        }
      />

      {error !== null && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading || row === null ? (
        <div className="flex items-center gap-2 py-12 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      ) : (
        <>
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <PhoneCall className="h-5 w-5" />
                    {row.lane ?? `Call ${row.id.slice(0, 8)}…`}
                  </CardTitle>
                  <CardDescription>
                    {row.bridge_kind.replace(/_/g, " ")} ·{" "}
                    {formatDateTime(row.started_at)}
                  </CardDescription>
                </div>
                <span
                  className={cn(
                    "inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize",
                    STATUS_PILL[row.status] ?? "bg-muted text-muted-foreground",
                  )}
                >
                  {row.status.replace(/_/g, " ")}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <Field label="Started" value={formatDateTime(row.started_at)} />
                <Field label="Ended" value={formatDateTime(row.ended_at)} />
                <Field label="Duration" value={formatDuration(row.duration_seconds)} />
                <Field
                  label="Exotel call sid"
                  value={row.exotel_call_sid ?? "—"}
                  mono
                />
                <Field label="Bridge kind" value={row.bridge_kind} />
                <Field
                  label="Bid id"
                  value={
                    <span className="font-mono text-xs" title={row.bid_id}>
                      {row.bid_id.slice(0, 8)}…
                    </span>
                  }
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Parties</CardTitle>
              <CardDescription>
                Caller (shipper) and callee (driver). Click either to open
                their full admin user view.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    Shipper
                  </div>
                  <Link
                    to={`/users/${row.shipper_id}`}
                    className="font-medium hover:underline"
                  >
                    {row.shipper_name ?? row.shipper_id.slice(0, 8)}
                  </Link>
                  <div className="text-xs text-muted-foreground font-mono">
                    {row.shipper_phone ?? "—"}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    Driver
                  </div>
                  <Link
                    to={`/users/${row.driver_id}`}
                    className="font-medium hover:underline"
                  >
                    {row.driver_name ?? row.driver_id.slice(0, 8)}
                  </Link>
                  <div className="text-xs text-muted-foreground font-mono">
                    {row.driver_phone ?? "—"}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recording</CardTitle>
              <CardDescription>
                Recordings live on Exotel and require basic-auth. Open the
                URL in a tab where your Exotel admin session is active, or
                copy it into an authenticated curl.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {row.recording_url === null ? (
                <div className="text-sm text-muted-foreground">
                  No recording captured for this call.
                </div>
              ) : (
                <div className="space-y-2">
                  <pre className="bg-muted/40 rounded-md p-3 text-xs break-all whitespace-pre-wrap">
                    {row.recording_url}
                  </pre>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void copyUrl(row.recording_url ?? "")}
                    >
                      <Copy className="mr-1 h-3.5 w-3.5" />
                      {copied ? "Copied!" : "Copy URL"}
                    </Button>
                    <Button asChild variant="outline" size="sm">
                      <a
                        href={row.recording_url}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        <ExternalLink className="mr-1 h-3.5 w-3.5" />
                        Open in new tab
                      </a>
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}): React.JSX.Element {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          "text-sm font-medium break-words",
          mono && "font-mono text-xs",
        )}
      >
        {value}
      </div>
    </div>
  );
}
